import type { IDocumentStatus } from '@/types/document'
import type { ITextDocumentVersionStatus } from '@/types/textDocument'
import { UNCLASSIFIED, type IDisplayClassification } from '@/lib/constants/documents'

export const DOCUMENT_STATUS: { [K in IDocumentStatus]: string } = {
  notApplicable: 'Not Applicable',
  notStarted: 'Not Started',
  inProgress: 'In progress',
  completed: 'Completed',
  approved: 'Approved',
}

export const TEXT_DOCUMENT_VERSION_STATUS: { [K in ITextDocumentVersionStatus]: string } = {
  draft: 'Draft',
  completed: 'Completed',
  approved: 'Approved',
}

export const DOCUMENT_CLASSIFICATION: { [K in IDisplayClassification]: string } = {
  Public: 'Public',
  Internal: 'Internal',
  Confidential: 'Confidential',
  'Commercial in Confidence': 'Commercial in Confidence',
  [UNCLASSIFIED]: 'Unclassified',
}

export const DOCUMENT_TABLE = {
  TITLE: 'Documents',
  STATUS: 'Status',
  VERSION: 'Version',
  NAME: 'Name',
  OWNER: 'Owner',
  LAST_UPDATED: 'Last Updated',
  CLUSTER: 'Cluster',
  ID: 'ID',
  TREATMENT: 'Treatment',
  SEARCH_PLACEHOLDER: 'Search documents...',
  NEW_DOCUMENT: 'New Document',
  BACK_TO_LIST: 'Go back',
  UNTITLED: 'Untitled document',
  ORGANIZATION_LOGO: 'Organization logo',
  EXPORT_PDF: 'Export PDF',
}
export const TEXT_DOCUMENT_VERSIONS = {
  CREATE_NEW_VERSION: 'Create new version',
  EDIT_DRAFT: (year: number) => `Edit draft ${year}`,
  TITLE_WITH_COUNT: (count: number) => `Qalea document (${count} ${count === 1 ? 'version' : 'versions'})`,
  VERSION_NUMBER: (versionNumber: number) => `v${versionNumber}`,
  EMPTY: 'No versions yet',
  REVIEW: 'Review',
  SUPERSEDED: 'Old approved',
  CREATED_BY: (name: string) => `Created by ${name}`,
  APPROVED_BY: (name: string) => `Approved by ${name}`,
  UNKNOWN_USER: 'Unknown user',
  APPROVE: 'Approve version',
  APPROVE_TITLE: 'Approve this version?',
  APPROVE_DESCRIPTION: 'Once approved this version becomes read-only. Further changes need a new version.',
  CHANGE_SUMMARY: 'Change summary',
  CHANGE_SUMMARY_FIELD: 'Change summary (optional)',
  CHANGE_SUMMARY_PLACEHOLDER: 'What changed in this version, and why?',
  SHARED_LINK_NOT_UPDATED: 'Saved, but the framework document link could not be updated. Please try again.',
  MARK_COMPLETED: 'Mark as completed',
  REOPEN: 'Reopen as draft',
}

export const TEXT_DOCUMENT_EDITOR = {
  UNAVAILABLE_TITLE: 'This document could not be loaded',
  UNAVAILABLE_BODY: 'We could not reach the editing service. Please reload the page in a moment to try again.',
}

// Emailed to someone mentioned with `@` in a text document or in one of its comments.
export const TEXT_DOCUMENT_MENTION = {
  EMAIL_SUBJECT: (authorName: string, documentName: string) => `${authorName} mentioned you in ${documentName}`,
  EMAIL_TEXT: (authorName: string, documentName: string, url: string) =>
    `${authorName} mentioned you in ${documentName}. Open the document: ${url}`,
  EMAIL_HTML: (authorName: string, documentName: string, url: string) => `
  <div style="font-family: Arial, sans-serif; line-height: 1.6; color: #333;">
    <p>${authorName} mentioned you in <strong>${documentName}</strong>.</p>
    <p><a href="${url}" style="color: #007bff; text-decoration: none;">Open the document</a></p>
    <p>Thank you,<br>Qalea Team</p>
  </div>
`,
}

export const TEXT_DOCUMENT_ATTRIBUTES = {
  LINKED_DOCUMENT: 'Compliance document',
  LINKED_TEXT_DOCUMENT: 'Text document',
  LINKED_TO: (documentName: string) => `linked to ${documentName}`,
  CLAIMED_ELSEWHERE: (documentName: string) =>
    `That text document is already linked to ${documentName}. Unlink it there first.`,
  UNLINKED: 'Not linked',
  REMOVE: 'Remove link',
}

export const DOCUMENT_COLUMN_FILTER = {
  ALL: 'All',
  CLEAR: 'Clear',
  SEARCH_PLACEHOLDER: 'Search...',
  NO_RESULTS: 'No results found.',
}
export const DOCUMENT_BULK_ACTIONS = {
  SELECTED: 'selected',
  DOCUMENT: 'document',
  DOCUMENTS: 'documents',
  UPDATING: 'Updating...',
  CLEAR: 'Clear',
}
export const DOCUMENT_OWNER_SELECTOR = {
  SELECT_OWNER: 'Select owner',
  SEARCH_USERS: 'Search users...',
  LOADING: 'Loading...',
  NO_USER_FOUND: 'No user found.',
}
export const DOCUMENT_STATUS_SELECTOR = {
  SELECT_STATUS: 'Select status',
}
export const DOCUMENT_FILTERS = {
  ALL: 'All',
  FILTER_BY_OWNER: 'Filter by Owner',
  FILTER_BY_CLUSTER: 'Filter by Cluster',
  FILTER_BY_STANDARD: 'Filter by Standard',
}

export const DOCUMENT_DETAIL = {
  ATTRIBUTES: 'Attributes',
  CONTENT: 'Document Content',
  DESCRIPTION: 'Document Description',
  EDIT: 'Edit Document',
  MISSING_DESCRIPTION: 'Missing document description',
  RELATED_CONTROLS: 'Related Controls',
  TITLE: 'Document',
  CUSTOM_FILE_UPLOAD: 'Upload Custom File',
  ADD_CUSTOM_LINK: 'Add Custom Link',
  NO_LINKS: 'No links added yet',
  NO_FILES: 'No files uploaded yet',
  NO_SHARED_FILE: 'No shared file available',
  DOWNLOAD_TEMPLATE: 'See Shared File',
  DOWNLOAD_CONTENT: 'Last Version',
  MISSING_CONTENT: '',
  NEVER_UPDATED: 'Never updated',
  DOCUMENT_UPDATED: 'Document updated',
  CUSTOM_LINKS: 'Custom Links',
  CUSTOM_FILES: 'Custom Files',
  SHARED_FILE: 'Shared File',
  CUSTOM_FILE_DESCRIPTION: 'You can upload your own document by clicking the button below.',
  UPLOADED_AT: 'Uploaded at',
  FILE_NOT_FOUND: 'File not found',
  LINK_LABEL: 'Label',
  LINK_LABEL_PLACEHOLDER: 'e.g., Documentation',
  LINK_URL: 'URL',
  LINK_URL_PLACEHOLDER: 'https://example.com',
  CANCEL: 'Cancel',
  ADD_LINK: 'Add Link',
  NO_EXTERNAL_LINKS: 'No external links added',
}

export const DOCUMENT_COMMENTS = {
  SHOW_COMMENTS: 'Show comments',
  HIDE_COMMENTS: 'Hide comments',
  ADD_COMMENT: 'Add comment',
  NEW_COMMENT_PLACEHOLDER: 'Write a comment...',
  EDIT_COMMENT_PLACEHOLDER: 'Edit comment...',
  REPLY_PLACEHOLDER: 'Add comment...',
  SAVE: 'Save',
  CANCEL: 'Cancel',
  EDITED: 'edited',
  ORIGINAL_CONTENT_DELETED: 'Original content deleted',
  RESOLVE: 'Resolve',
  REOPEN: 'Re-open',
  EDIT_COMMENT: 'Edit comment',
  DELETE_COMMENT: 'Delete comment',
  MORE_ACTIONS: 'More actions',
}
